import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { number, shape } from 'prop-types'
import { useForm } from 'react-hook-form'
import CurrencyFormat from 'react-currency-format'
import useInternationalization from '@hooks/useInternationalization'

import useCoupon from '@hooks/useCoupon'

import Divider from '@components/Divider'

import * as Style from './OfferCard.style'

function Card({ contact }) {
  const { handleSubmit, register } = useForm()
  const { handleCoupon, isCouponValid, hasCoupon } = useCoupon()

  const [couponForm, setCouponForm] = useState({
    isOpened: false,
  })

  const {
    formats: { currency },
  } = useInternationalization()

  function openCouponField() {
    setCouponForm({ isOpened: true })
  }

  function closeCouponField() {
    setCouponForm({ isOpened: false })
  }

  async function handleCouponForm({ coupon }) {
    await handleCoupon(contact.employeesNumber, coupon)
  }

  useEffect(() => {
    if (isCouponValid) {
      closeCouponField()
    }
  }, [isCouponValid])

  return (
    <Style.Card>
      <Style.CardHeader>
        <Style.CardTitle>
          <FormattedMessage id="smb_acquisition_checkout_summary.title.summary" />
        </Style.CardTitle>
      </Style.CardHeader>

      <Style.CardContent>
        <Style.ContentRow>
          <Style.PriceDescription>
            <FormattedMessage
              id="smb_acquisition_checkout_summary.description.benefit_value"
              values={{
                numberOfEmployees: contact.employeesNumber,
              }}
            />
          </Style.PriceDescription>

          <Style.PriceValue>
            <CurrencyFormat
              prefix={currency.moneySign}
              value={contact.proposalValueWithoutDiscount}
              displayType="text"
              thousandSeparator={currency.thousandSeparator}
              decimalSeparator={currency.decimalSeparator}
              decimalScale={2}
              fixedDecimalScale
            />
          </Style.PriceValue>
        </Style.ContentRow>

        <Style.ContentRow>
          <Style.PriceDescription>
            <FormattedMessage id="smb_acquisition_checkout_summary.description.discount_coupon" />
          </Style.PriceDescription>

          {contact.discount < 0 ? (
            <Style.PriceValue>
              <CurrencyFormat
                prefix={currency.moneySign}
                value={contact.discount}
                displayType="text"
                thousandSeparator={currency.thousandSeparator}
                decimalSeparator={currency.decimalSeparator}
                decimalScale={2}
                fixedDecimalScale
              />
            </Style.PriceValue>
          ) : (
            <Style.LinkButton onClick={openCouponField}>
              <Style.LinkButtonText>
                <FormattedMessage id="smb_acquisition_discount_coupon.call_to_action.open" />
              </Style.LinkButtonText>
            </Style.LinkButton>
          )}
        </Style.ContentRow>
      </Style.CardContent>

      <Style.DividerWrapper>
        <Divider />
      </Style.DividerWrapper>

      {contact.proposalValueWithoutDiscount > contact.proposalValue && (
        <Style.ContentRow>
          <Style.PriceDescription>
            <FormattedMessage id="smb_acquisition_checkout_summary.description.total_without_discount" />
          </Style.PriceDescription>

          <Style.DependentsValue>
            <Style.TotalWrapper>
              <em>{currency.moneySign}</em>
              <CurrencyFormat
                value={contact.proposalValueWithoutDiscount}
                displayType="text"
                thousandSeparator={currency.thousandSeparator}
                decimalSeparator={currency.decimalSeparator}
                decimalScale={2}
                fixedDecimalScale
              />
            </Style.TotalWrapper>
          </Style.DependentsValue>
        </Style.ContentRow>
      )}

      <Style.ContentRow>
        <Style.PriceDescription bold>
          <FormattedMessage id="smb_acquisition_checkout_summary.description.total_value" />
        </Style.PriceDescription>

        <Style.PriceValue dark>
          <Style.TotalWrapper>
            <em>{currency.moneySign}</em>
            <CurrencyFormat
              value={contact.proposalValue}
              displayType="text"
              thousandSeparator={currency.thousandSeparator}
              decimalSeparator={currency.decimalSeparator}
              decimalScale={2}
              fixedDecimalScale
            />
            /
            <FormattedMessage id="smb_acquisition_commons.frequency.monthly" />
          </Style.TotalWrapper>
        </Style.PriceValue>
      </Style.ContentRow>
      <Style.CardActions>
        {couponForm.isOpened && (
          <Style.CouponWrapper>
            <Style.Form>
              <Style.Input name="coupon" ref={register} />

              <Style.DiscountButton onClick={handleSubmit(handleCouponForm)}>
                <Style.ButtonText>
                  <FormattedMessage id="smb_acquisition_discount_coupon.call_to_action.apply" />
                </Style.ButtonText>
              </Style.DiscountButton>
            </Style.Form>

            <Style.CouponRow>
              {hasCoupon && !isCouponValid && (
                <Style.ErrorMessage>
                  <FormattedMessage id="smb_acquisition_discount_coupon.messages.not_valid" />
                </Style.ErrorMessage>
              )}

              <Style.CancelCoupon onClick={closeCouponField}>
                <FormattedMessage id="smb_acquisition_discount_coupon.call_to_action.cancel" />
              </Style.CancelCoupon>
            </Style.CouponRow>
          </Style.CouponWrapper>
        )}
      </Style.CardActions>
    </Style.Card>
  )
}

Card.propTypes = {
  contact: shape({
    id: number,
    employeesNumber: number,
  }).isRequired,
}

export default Card
